"use client";
import React, { useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { cn } from "@/lib/utils";
import SectionHeader from "../common/section-header";
import { DotPattern } from "../magicui/dot-pattern";
import clsx from "clsx";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import { SanityDocument } from "next-sanity";
import { Calendar } from "lucide-react";

interface TimelineSectionProps {
  timeline: SanityDocument[];
}

function TimelineSection({ timeline }: Readonly<TimelineSectionProps>) {
  const timelineRef = useRef(null);
  useGSAP(
    () => {
      const cards: HTMLElement[] = gsap.utils.toArray(".timeline-cards");
      gsap.registerPlugin(ScrollTrigger);
      cards.forEach((card, i) => {
        const direction = i % 2 === 0 ? 50 : -50;
        gsap.fromTo(
          `.timeline-${card.id}`,
          { autoAlpha: 0, x: direction },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.5,
            scrollTrigger: {
              trigger: `.timeline-${card.id}`,
              start: "top bottom-=200",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    },
    { scope: timelineRef },
  );

  return (
    <section
      ref={timelineRef}
      className="relative overflow-hidden bg-slate-50 px-8 py-20 md:px-10"
    >
      <DotPattern
        width={20}
        height={20}
        cx={1}
        cy={1}
        cr={1}
        className={cn(
          "[mask-image:linear-gradient(to_bottom_right,white,transparent,white)]",
        )}
      />
      <SectionHeader
        highlightedTitle="Our Journey,"
        title="A Timeline of Excellence"
        bodyText="At Rake Engineering Consultancy, our journey is marked by milestones that reflect our commitment to precision engineering and consultancy. Each event in our timeline showcases our growth, achievements, and dedication to delivering exceptional services."
      />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 h-full w-0.5 -translate-x-px transform bg-orange-600"></div>

          <div className="space-y-12">
            {timeline.map((event, index) => (
              <div
                key={event.year}
                className={clsx(
                  index % 2 === 0 ? "sm:justify-start" : "sm:justify-end",
                  "relative flex items-center",
                )}
              >
                {/* Timeline dot */}
                <div
                  className={clsx(
                    index === timeline.length - 1 && "hidden xl:block",
                    "absolute -bottom-8 left-1/2 z-10 h-4 w-4 -translate-x-1/2 transform rounded-full border-4 border-gray-50 bg-orange-600 xl:bottom-1/2 xl:translate-y-1/2",
                  )}
                ></div>

                {/* Content card */}
                <Card
                  id={`card-${index}`}
                  className={clsx(
                    index % 2 === 0
                      ? "md:mr-auto md:pr-8"
                      : "md:ml-auto md:pl-8",
                    `timeline-cards timeline-card-${index}`,
                    "w-full max-w-md rounded-none border-gray-200 bg-white shadow-lg",
                  )}
                >
                  <CardContent className="p-6">
                    <div className="mb-3 flex items-center">
                      <Calendar className="mr-3 h-6 w-6 text-orange-600" />
                      <span className="text-lg font-bold text-orange-600">
                        {event.month ?? ""} {event.year}
                      </span>
                    </div>
                    <h3 className="mb-2 text-xl font-bold text-gray-900">
                      {event.title}
                    </h3>
                    <p className="text-gray-600">{event.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TimelineSection;
